import {LoadingButton} from "@mui/lab";
import {
    Alert,
    Button,
    Chip,
    Divider,
    FormControl,
    Icon,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
} from "@mui/material";
import {ChangeEvent, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {GatewayNumberDto, GatewayNumberStatus} from "../../../api/psc";
import {AppDatePicker} from "../../../components/exports";
import SectionHeader from "../../../components/shared/SectionHeader";
import {RootState} from "../../../slices/Store";
import {
    getCurrentDate,
    getHumanReadableDiff,
    processSmsCampaignDateChange,
} from "../../../utils/date";
import {fixMobileNumber} from "../../../utils/language";
import {ValidateSmsCampaignSettingStage} from "../../../validations/SmsCampaignValidation";
import {
    SmsCampaignType,
    setSmsCampaignDefinition,
    setSmsCampaignPhoneNumbers,
    smsCampaignNextStep,
    smsCampaignPrevStep,
} from "../smsCampaignSlice/SmsCampaignSlice";
import SmsCampaignTimePicker from "./SmsCampaignTimePicker";
import SmsGatewayNumberItem from "./SmsGatewayNumberItem";

export default function SmsCampaignSettings() {
    const dispatch = useDispatch();

    const [error, setError] = useState<string | null>(null);
    const [phoneNumber, setPhoneNumber] = useState<string>("");

    const smsCampaign = useSelector((state: RootState) => state.smsCampaign);
    const shared = useSelector((state: RootState) => state.shared);

    // -----------------------------------------------------------------------------------------------------//
    // -----------------------------------  filter change handlers -----------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    const handleGatewayNumberChange = (event: SelectChangeEvent<number>) => {
        dispatch(
            setSmsCampaignDefinition({
                ...smsCampaign.definition,
                gatewayNumberId: event.target.value as number,
            })
        );
    };

    const handleDueDateTypeChange = (event: SelectChangeEvent<number>) => {
        dispatch(
            setSmsCampaignDefinition({
                ...smsCampaign.definition,
                scheduleDate:
                    event.target.value === 0 ? getCurrentDate() : null,
            })
        );
    };

    const handleDateChange = (dueDate: string) => {
        dispatch(
            setSmsCampaignDefinition({
                ...smsCampaign.definition,
                scheduleDate: processSmsCampaignDateChange(dueDate),
            })
        );
    };

    const handleTimeChange = (dueDate: string) => {
        dispatch(
            setSmsCampaignDefinition({
                ...smsCampaign.definition,
                scheduleDate: dueDate,
            })
        );
    };

    const handlePhoneNumberChange = (
        elem: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const phoneNumber = fixMobileNumber(elem.target.value);
        if (/^09\d{9}$/.test(phoneNumber)) {
            const updatedPhoneNumbers = [
                ...smsCampaign.mobileNumbers,
                phoneNumber,
            ];
            dispatch(setSmsCampaignPhoneNumbers(updatedPhoneNumbers));
            setPhoneNumber("");
        } else {
            setPhoneNumber(phoneNumber);
        }
    };

    const removePhoneNumber = (pn: string) => {
        const updatedPhoneNumbers = [...smsCampaign.mobileNumbers];
        const indexOfPnToRemove = updatedPhoneNumbers.indexOf(pn);

        if (indexOfPnToRemove !== -1) {
            updatedPhoneNumbers.splice(indexOfPnToRemove);
            dispatch(setSmsCampaignPhoneNumbers(updatedPhoneNumbers));
        }
    };

    // -----------------------------------------------------------------------------------------------------//
    // ---------------------------------  filter change handlers end ---------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    // -----------------------------------------------------------------------------------------------------//
    // ------------------------------------  step change handlers ------------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    const handlePrevious = () => {
        dispatch(smsCampaignPrevStep());
    };

    const handleNext = () => {
        const errorMessage = ValidateSmsCampaignSettingStage(smsCampaign);

        if (errorMessage) {
            setError(errorMessage);
            return;
        }

        dispatch(smsCampaignNextStep());
    };

    // -----------------------------------------------------------------------------------------------------//
    // ----------------------------------  step change handlers end ----------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    // we only show private gateway numbers when type is "CampaignWithProjectFilter"
    // otherwise we can show all gateway numbers
    const filterGatewayNumbersBasedOnSmsCampaignType = (
        gn: GatewayNumberDto
    ) => {
        if (smsCampaign.smsCampaignType === SmsCampaignType.CampaignWithProjectFilter) {
            return true;
        }

        // means this account has unrestricted access to sms campaigns
        if ((shared.accountDetail.options & 4) == 4) {
            return true;
        }

        return gn.status !== GatewayNumberStatus.PRIVATE;
    };

    return (
        <Stack spacing={2}>
            <SectionHeader title="تنظیمات کمپین"/>

            <FormControl fullWidth>
                <InputLabel>انتخاب سرشماره</InputLabel>
                <Select
                    label="انتخاب سرشماره"
                    value={smsCampaign.definition.gatewayNumberId ?? ""}
                    onChange={handleGatewayNumberChange}
                >
                    {smsCampaign?.gatewayNumbers
                        ?.filter(filterGatewayNumbersBasedOnSmsCampaignType)
                        .map((gn) => {
                            return (
                                <MenuItem key={gn.id} value={gn.id}>
                                    <SmsGatewayNumberItem gatewayNumber={gn}/>
                                </MenuItem>
                            );
                        })}
                </Select>
            </FormControl>

            <FormControl fullWidth>
                <InputLabel>تاریخ ارسال</InputLabel>

                <Select
                    label="تاریخ ارسال"
                    value={smsCampaign.definition.scheduleDate ? 0 : 1}
                    onChange={handleDueDateTypeChange}
                >
                    <MenuItem value={1}>ارسال بلافاصله</MenuItem>
                    <MenuItem value={0}>تاریخ انتخابی</MenuItem>
                </Select>
            </FormControl>

            {smsCampaign.definition.scheduleDate && (
                <>
                    <AppDatePicker
                        label="تاریخ ارسال"
                        onChange={handleDateChange}
                        initialValue={smsCampaign.definition.scheduleDate}
                    />

                    <SmsCampaignTimePicker
                        label="ساعت ارسال"
                        onChange={handleTimeChange}
                        initialValue={smsCampaign.definition.scheduleDate}
                    />

                    <Alert severity="info">
                        {getHumanReadableDiff(
                            smsCampaign.definition.scheduleDate
                        )}
                    </Alert>
                </>
            )}

            {smsCampaign.gatewayNumbers?.find(
                (gn) => gn.id === smsCampaign.definition.gatewayNumberId
            )?.status === GatewayNumberStatus.PUBLIC && (
                <>
                    <TextField
                        fullWidth
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        label="شماره تلفن های انتخابی"
                        variant="outlined"
                    />

                    {smsCampaign.mobileNumbers.length > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {smsCampaign.mobileNumbers.map((pn) => (
                                <Chip
                                    key={pn}
                                    color="secondary"
                                    label={pn}
                                    onDelete={() => removePhoneNumber(pn)}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <Alert severity="info">
                            شماره موبایل های مورد نظر خود را با فرمت 09XXXXXXXXX
                            وارد کنید.
                        </Alert>
                    )}
                </>
            )}

            <Divider/>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" justifyContent="space-between">
                <Button
                    onClick={handlePrevious}
                    variant="outlined"
                    startIcon={<Icon>navigate_next</Icon>}
                >
                    مرحله قبل
                </Button>

                <LoadingButton
                    onClick={handleNext}
                    variant="contained"
                    color="primary"
                    endIcon={<Icon>navigate_before</Icon>}
                >
                    مرحله بعد
                </LoadingButton>
            </Stack>
        </Stack>
    );
}
